/**
 * Styles for OnboardRegisterScreen component (Create an account)
 * Responsive styles for mobile (xs) and desktop (md)
 */
import { designTokens } from '../../../../theme';

export const registerStyles = {
    pageContainer: {
        minHeight: '100vh',
        backgroundColor: designTokens.colors.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, md: 4 },
    },
    logo: {
        marginBottom: { xs: 2, md: 3 },
    },
    logoSize: {
        width: { xs: 50, md: 64 },
        height: { xs: 50, md: 64 },
    },
    logoCircle: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: { xs: 50, md: 64 },
        height: { xs: 50, md: 64 },
    },
    logoIcon: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: 29, md: 42 },
        height: { xs: 22, md: 32 },
    },
    card: {
        backgroundColor: designTokens.colors.cardBackground,
        borderRadius: { xs: '16.5px', md: `${designTokens.spacing.cardBorderRadius}px` },
        padding: {
            xs: '22px 31px',
            md: `${designTokens.spacing.cardPaddingY}px ${designTokens.spacing.cardPaddingX}px`,
        },
        width: '100%',
        maxWidth: `${designTokens.spacing.cardWidth}px`,
        boxSizing: 'border-box',
        border: { xs: '0.55px solid rgba(102, 102, 102, 0.15)', md: 'none' },
    },
    title: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: '26px', md: '36px' },
        fontWeight: 600,
        lineHeight: { xs: '26px', md: '36px' },
        letterSpacing: '-0.02em',
        color: designTokens.colors.textPrimary,
        textAlign: 'center',
        marginBottom: { xs: 1, md: 2 },
    },
    subtitle: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: '12px', md: '16px' },
        fontWeight: 400,
        letterSpacing: '-0.02em',
        color: designTokens.colors.textSecondary,
        textAlign: 'center',
        marginBottom: { xs: 2, md: 3 },
    },
    loginLink: {
        color: designTokens.colors.link,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 400,
    },
    label: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: 12, md: designTokens.typography.label.fontSize },
        fontWeight: designTokens.typography.label.fontWeight,
        letterSpacing: designTokens.typography.label.letterSpacing,
        color: designTokens.colors.textSecondary,
        marginBottom: 0.5,
    },
    optionalLabel: {
        color: 'rgba(97, 96, 99, 0.5)',
        fontWeight: 400,
    },
    input: {
        '& .MuiOutlinedInput-root': {
            height: { xs: '40px', md: '56px' },
            borderRadius: { xs: '6.6px', md: '12px' },
            backgroundColor: '#FFFFFF',
            '& fieldset': {
                borderColor: 'rgba(102, 102, 102, 0.35)',
            },
            '&:hover fieldset': {
                borderColor: designTokens.colors.divider,
            },
            '&.Mui-focused fieldset': {
                borderColor: designTokens.colors.primaryButton,
            },
        },
        '& .MuiFormHelperText-root': {
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: '10px', md: '12px' },
            marginLeft: 0,
            marginTop: '4px',
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: '#F44336',
        },
    },
    inputHalf: {
        width: 'calc(50% - 8px)',
        '& .MuiOutlinedInput-root': {
            height: { xs: '40px', md: '56px' },
            borderRadius: { xs: '6.6px', md: '12px' },
            backgroundColor: '#FFFFFF',
            '& fieldset': {
                borderColor: 'rgba(102, 102, 102, 0.35)',
            },
            '&:hover fieldset': {
                borderColor: designTokens.colors.divider,
            },
            '&.Mui-focused fieldset': {
                borderColor: designTokens.colors.primaryButton,
            },
        },
    },
    rowContainer: {
        display: 'flex',
        gap: 2,
        marginBottom: 2,
    },
    fieldContainer: {
        flex: 1,
    },
    selectInput: {
        height: { xs: '40px', md: '56px' },
        borderRadius: { xs: '6.6px', md: '12px' },
        backgroundColor: '#FFFFFF',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(102, 102, 102, 0.35)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: designTokens.colors.divider,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: designTokens.colors.primaryButton,
        },
        '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: 12, md: designTokens.typography.label.fontSize },
            fontWeight: designTokens.typography.label.fontWeight,
            letterSpacing: designTokens.typography.label.letterSpacing,
            color: designTokens.colors.textSecondary,
        },
    },
    occupationMenuItem: {
        color: designTokens.colors.textSecondary,
        minHeight: { xs: '40px', md: '54px' },
    },
    occupationMenuText: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: 12, md: designTokens.typography.label.fontSize },
        fontWeight: designTokens.typography.label.fontWeight,
        letterSpacing: designTokens.typography.label.letterSpacing,
        color: designTokens.colors.textSecondary,
    },
    continueButton: {
        backgroundColor: '#FF669B !important',
        color: '#FFFFFF !important',
        borderRadius: { xs: '22px', md: '32px' },
        height: { xs: '35px', md: '64px' },
        fontSize: { xs: '14px', md: '22px' },
        fontWeight: '500 !important',
        textTransform: 'none !important',
        fontFamily: 'Inter, sans-serif !important',
        letterSpacing: '-0.02em',
        marginTop: 2,
        '&:hover': {
            backgroundColor: '#e65588 !important',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(255, 102, 155, 0.5) !important',
            color: 'rgba(255, 255, 255, 0.7) !important',
        },
    },
    termsText: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: '9px', md: '14px' },
        fontWeight: 400,
        letterSpacing: '-0.02em',
        color: designTokens.colors.textMuted,
        textAlign: 'center',
        marginTop: { xs: 1, md: 2 },
        lineHeight: { xs: '12px', md: 'normal' },
    },
    termsLink: {
        color: 'rgba(97, 96, 99, 0.5)',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 400,
    },
    // Password validation styles
    validationContainer: {
        display: { xs: 'grid', md: 'flex' },
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))' },
        flexWrap: { md: 'wrap' },
        gap: { xs: '6px 10px', md: 1.5 },
        marginTop: 1,
        marginBottom: 2,
    },
    validationItem: {
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 0.5,
        minWidth: 0,
    },
    validationDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },
    validationDotDefault: {
        backgroundColor: '#666666',
    },
    validationDotSuccess: {
        backgroundColor: '#4CAF50',
    },
    validationDotError: {
        backgroundColor: '#F44336',
    },
    validationText: {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: '8px', md: '14px' },
        lineHeight: { xs: '10px', md: 'normal' },
        fontWeight: 400,
        color: 'rgba(97, 96, 99, 0.8)',
        letterSpacing: '-0.02em',
    },
};
